import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Switch,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Alert,
  Card,
  CardContent,
  OutlinedInput,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  CloudUpload as UploadIcon,
  Inventory as InventoryIcon,
  LocalOffer as PriceIcon,
  Receipt as TaxIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  CurrencyExchange as CurrencyIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { productService, mastersService } from '../utils/apiService';
import { useAuth } from '../context/AuthContext';
import { translateToArabic, debounce } from '../utils/translateService';
import settingsService from '../services/settingsService';
import image1 from "../assets/riyal.png";

const API_URL = process.env.REACT_APP_API_URL || 'https://zatca-simple-invoice-main.onrender.com/api';

const NewItem = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentTenant } = useAuth();
  const isEditMode = Boolean(id);

  const [activeTab, setActiveTab] = useState(0);
  const [autoCodeEnabled, setAutoCodeEnabled] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [itemGroups, setItemGroups] = useState([]);
  const [units, setUnits] = useState([]);
  const [formData, setFormData] = useState({
    productName: '',
    productCode: '',
    alternateName: '',
    barcode: '',
    altBarcode: '',
    group: '',
    itemType: 'goods',
    description: '',
    basicUnit: '',
    alternateUnit: '',
    purchaseRate: '',
    altPurchaseRate: '',
    salesRate: '',
    altSalesRate: '',
    hsnCode: '',
    taxRate: 15,
    taxType: 'standard',
    isActive: true
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);

  // Debounced translation function
  const debouncedTranslate = React.useCallback(
    debounce(async (text) => {
      if (!text || !text.trim()) {
        return;
      }

      setIsTranslating(true);
      try {
        const arabicText = await translateToArabic(text);
        if (arabicText) {
          setFormData(prev => ({
            ...prev,
            alternateName: arabicText
          }));
        }
      } catch (error) {
        console.error('Translation failed:', error);
      } finally {
        setIsTranslating(false);
      }
    }, 800), // Wait 800ms after user stops typing
    []
  );

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        // Wait for tenant information to be available
        if (!currentTenant) {
          console.log('Waiting for tenant information...');
          return;
        }

        const tenantId = currentTenant.id || currentTenant._id;
        if (!tenantId) {
          console.error('No tenant ID available');
          setError('Unable to load item groups: No tenant ID available');
          return;
        }

        console.log('Initializing with tenant:', tenantId);

        await fetchSettings();

        // Load data
        if (isEditMode) {
          await fetchProduct();
        }
        await fetchMasters();
      } catch (err) {
        console.error('Initialization error:', err);
        setError(err.message || 'Failed to initialize component');
      }
    };

    initializeComponent();
  }, [id, currentTenant]);

  const fetchSettings = async () => {
    try {
      const response = await settingsService.getSettings();
      if (response.success && response.data) {
        const isAutoCode = response.data.general?.itemAutoCode;
        setAutoCodeEnabled(isAutoCode);
        if (isAutoCode && !isEditMode) {
          try {
            const autoCodeResponse = await settingsService.getAutoCode('item');
            if (autoCodeResponse.success && autoCodeResponse.data) {
              setFormData(prev => ({
                ...prev,
                productCode: autoCodeResponse.data
              }));
            }
          } catch (codeErr) {
            console.error('Error fetching auto code:', codeErr);
            // Fallback to timestamp based if API fails
            setFormData(prev => ({
              ...prev,
              productCode: `ITEM-${Math.floor(Date.now() / 1000).toString().slice(-6)}`
            }));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const fetchProduct = async () => {
    try {
      const product = await productService.getProduct(id);
      setFormData({
        productName: product.productName || '',
        productCode: product.productCode || '',
        alternateName: product.alternateName || '',
        barcode: product.barcode || '',
        altBarcode: product.altBarcode || '',
        group: product.groupId || '',
        itemType: product.itemType || 'goods',
        description: product.description || '',
        basicUnit: product.basicUnitId || '',
        alternateUnit: product.alternateUnitId || '',
        purchaseRate: product.purchaseRate || '',
        altPurchaseRate: product.altPurchaseRate || '',
        salesRate: product.salesRate || '',
        altSalesRate: product.altSalesRate || '',
        hsnCode: product.hsnCode || '',
        taxRate: product.taxRate || (product.taxType === 'standard' ? 15 : 0),
        taxType: product.taxType || 'standard',
        isActive: product.isActive !== undefined ? product.isActive : true
      });
    } catch (error) {
      console.error('Error fetching product:', error);
      setError(error.message || 'Failed to fetch product');
    }
  };

  const fetchMasters = async () => {
    try {
      if (!currentTenant) {
        console.error('No tenant information available');
        setError('Unable to load master data: No tenant information available');
        return;
      }

      const tenantId = currentTenant.id || currentTenant._id;
      if (!tenantId) {
        console.error('No tenant ID available');
        setError('Unable to load master data: No tenant ID available');
        return;
      }

      console.log('Fetching masters for tenant:', tenantId);
      const masters = await mastersService.getAllMasters(tenantId);

      if (masters.itemGroups) {
        console.log('Loaded item groups:', masters.itemGroups);
        setItemGroups(masters.itemGroups);
      }

      if (masters.units) {
        console.log('Loaded units:', masters.units);
        setUnits(masters.units);
      } else {
        console.error('No units found in response');
        setError('Failed to load units');
      }
    } catch (err) {
      console.error('Error fetching masters:', err);
      setError(err.message || 'Failed to fetch master data');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Auto-translate when product name changes
    if (name === 'productName' && value.trim()) {
      debouncedTranslate(value);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.productCode.trim()) {
      errors.productCode = 'Product code is required';
    }

    if (!formData.productName.trim()) {
      errors.productName = 'Product name is required';
    }

    if (!formData.alternateName.trim()) {
      errors.alternateName = 'Arabic name is required';
    }

    if (!formData.group) {
      errors.group = 'Group is required';
    }

    if (!formData.basicUnit) {
      errors.basicUnit = 'Basic unit is required';
    }

    // Rate validations
    const purchaseRate = parseFloat(formData.purchaseRate);
    const salesRate = parseFloat(formData.salesRate);

    // Basic required validations
    if (!salesRate || salesRate <= 0) {
      errors.salesRate = 'Sales rate is required and must be greater than 0';
    }

    // Only validate rate relationships if purchase rate is provided
    if (purchaseRate && salesRate) {
      if (purchaseRate > salesRate) {
        errors.purchaseRate = `Purchase rate (${purchaseRate}) cannot be greater than Sales rate (${salesRate})`;
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    setError(null);

    try {
      if (isEditMode) {
        await productService.updateProduct(id, formData);
      } else {
        await productService.createProduct(formData);
      }
      navigate('/items');
    } catch (err) {
      setError(err.message || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/items');
  };

  const handleTaxTypeChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      taxType: value,
      taxRate: value === 'standard' ? 15 : 0
    }));
  };

  // Update the common styling for all textboxes and dropdowns
  const controlStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      height: 36,
    },
    '& .MuiInputBase-input': {
      py: 0.75,
      fontSize: '0.875rem'
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem'
    },
    '& .MuiSelect-select': {
      py: 0.75
    }
  };

  // Update the multiline textbox style
  const multilineStyle = {
    ...controlStyle,
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      minHeight: 36,
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 0.5,
          borderRadius: '4px',
          maxWidth: '1800px',
          margin: '0 auto',
          boxShadow: 'none',
          border: 'none'
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={handleCancel}
              sx={{ mr: 0.5, bgcolor: 'rgba(67, 97, 238, 0.1)' }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold" sx={{ fontSize: '1.25rem' }}>
                {isEditMode ? 'Edit Item' : 'New Item'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.875rem' }}>
                {isEditMode ? 'Update item information' : 'Add a new item to your inventory'}
              </Typography>
            </Box>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 0.5,
              mb: 0.5,
              borderRadius: '4px',
              boxShadow: 'none',
              border: 'none',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
              },
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                mb: 0.5,
                minHeight: 36,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: 120,
                  py: 0.5,
                  minHeight: 36,
                  fontSize: '0.875rem'
                }
              }}
            >
              <Tab label="Basic Information" />
              <Tab label="Alternative UOM" />
            </Tabs>

            <Grid container spacing={0.5}>
              {/* Basic Information Tab */}
              {activeTab === 0 && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Product Code"
                      name="productCode"
                      value={formData.productCode}
                      onChange={handleChange}
                      fullWidth
                      required
                      margin="dense"
                      size="small"
                      placeholder="Enter product code"
                      sx={controlStyle}
                      autoComplete="off"
                      autoFocus={!autoCodeEnabled}
                      disabled={autoCodeEnabled || isEditMode}
                      error={!!validationErrors.productCode}
                      helperText={validationErrors.productCode}
                    />

                    <TextField
                      label="Product Name"
                      name="productName"
                      value={formData.productName}
                      onChange={handleChange}
                      fullWidth
                      required
                      margin="dense"
                      size="small"
                      placeholder="Enter product name"
                      sx={controlStyle}
                      autoComplete="off"
                    />

                    <TextField
                      label="Arabic Name"
                      name="alternateName"
                      value={formData.alternateName}
                      onChange={handleChange}
                      fullWidth
                      required
                      margin="dense"
                      size="small"
                      placeholder="Enter arabic name (auto-translates from Product Name)"
                      sx={controlStyle}
                      autoComplete="off"
                      error={!!validationErrors.alternateName}
                      helperText={validationErrors.alternateName || (isTranslating ? 'Translating...' : '')}
                      InputProps={{
                        endAdornment: isTranslating ? (
                          <InputAdornment position="end">
                            <Chip
                              label="Translating..."
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </InputAdornment>
                        ) : null
                      }}
                    />

                    <TextField
                      label="Barcode"
                      name="barcode"
                      value={formData.barcode}
                      onChange={handleChange}
                      fullWidth
                      margin="dense"
                      size="small"
                      placeholder="Enter barcode"
                      sx={controlStyle}
                      autoComplete="off"
                    />

                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel sx={{ fontSize: '0.875rem' }}>Item Type</InputLabel>
                      <Select
                        name="itemType"
                        value={formData.itemType}
                        onChange={handleChange}
                        label="Item Type"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 0,
                            height: 36,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: 0,
                          },
                          '& .MuiSelect-select': {
                            py: 0.75,
                            fontSize: '0.875rem',
                            borderRadius: 0,
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <MenuItem value="goods" sx={{ fontSize: '0.875rem' }}>Goods</MenuItem>
                        <MenuItem value="service" sx={{ fontSize: '0.875rem' }}>Service</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth margin="dense" size="small" required error={!!validationErrors.group}>
                      <InputLabel sx={{ fontSize: '0.875rem' }}>Group</InputLabel>
                      <Select
                        name="group"
                        value={formData.group || ''}
                        onChange={handleChange}
                        label="Group"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 0,
                            height: 36,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: 0,
                          },
                          '& .MuiSelect-select': {
                            py: 0.75,
                            fontSize: '0.875rem',
                            borderRadius: 0,
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
                          <em>Select a group</em>
                        </MenuItem>
                        {itemGroups.map((group) => (
                          <MenuItem key={group._id} value={group._id} sx={{ fontSize: '0.875rem' }}>
                            {group.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {validationErrors.group && (
                        <FormHelperText error>{validationErrors.group}</FormHelperText>
                      )}
                    </FormControl>

                    <TextField
                      label="Description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={4}
                      margin="dense"
                      size="small"
                      placeholder="Enter item description"
                      sx={multilineStyle}
                      autoComplete="off"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isActive}
                          onChange={handleChange}
                          name="isActive"
                          color="primary"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#4361ee',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#4361ee',
                            }
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {formData.isActive ? (
                            <>
                              <CheckCircleIcon sx={{ color: 'success.main', mr: 1, fontSize: '1rem' }} />
                              <Typography sx={{ fontSize: '0.875rem' }}>Active</Typography>
                            </>
                          ) : (
                            <>
                              <CancelIcon sx={{ color: 'error.main', mr: 1, fontSize: '1rem' }} />
                              <Typography sx={{ fontSize: '0.875rem' }}>Inactive</Typography>
                            </>
                          )}
                        </Box>
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="dense" size="small" required error={!!validationErrors.basicUnit}>
                      <InputLabel sx={{ fontSize: '0.875rem' }}>Basic Unit</InputLabel>
                      <Select
                        name="basicUnit"
                        value={formData.basicUnit}
                        onChange={handleChange}
                        label="Basic Unit"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 0,
                            height: 36,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: 0,
                          },
                          '& .MuiSelect-select': {
                            py: 0.75,
                            fontSize: '0.875rem',
                            borderRadius: 0,
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
                          <em>Select a unit</em>
                        </MenuItem>
                        {units.map((unit) => (
                          <MenuItem key={unit._id} value={unit._id} sx={{ fontSize: '0.875rem' }}>
                            {unit.name} ({unit.symbol})
                          </MenuItem>
                        ))}
                      </Select>
                      {validationErrors.basicUnit && (
                        <FormHelperText error>{validationErrors.basicUnit}</FormHelperText>
                      )}
                    </FormControl>

                    <TextField
                      label="Purchase Rate"
                      name="purchaseRate"
                      value={formData.purchaseRate}
                      onChange={handleChange}
                      fullWidth
                      margin="dense"
                      size="small"
                      type="number"
                      placeholder="Enter purchase rate"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <img src={image1} alt="currency" style={{ width: '16px', height: '16px' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={controlStyle}
                      autoComplete="off"
                      error={!!validationErrors.purchaseRate}
                      helperText={validationErrors.purchaseRate}
                    />

                    <TextField
                      label="Sales Rate"
                      name="salesRate"
                      value={formData.salesRate}
                      onChange={handleChange}
                      fullWidth
                      required
                      margin="dense"
                      size="small"
                      type="number"
                      placeholder="Enter sales rate"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">  <img src={image1} alt="currency" style={{ width: '16px', height: '16px' }} /></InputAdornment>
                        ),
                      }}
                      sx={controlStyle}
                      autoComplete="off"
                      error={!!validationErrors.salesRate}
                      helperText={validationErrors.salesRate}
                    />

                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel sx={{ fontSize: '0.875rem' }}>Tax Type</InputLabel>
                      <Select
                        name="taxType"
                        value={formData.taxType}
                        onChange={handleTaxTypeChange}
                        label="Tax Type"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 0,
                            height: 36,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: 0,
                          },
                          '& .MuiSelect-select': {
                            py: 0.75,
                            fontSize: '0.875rem',
                            borderRadius: 0,
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <MenuItem value="standard" sx={{ fontSize: '0.875rem' }}>Standard</MenuItem>
                        <MenuItem value="exempt" sx={{ fontSize: '0.875rem' }}>Exempt</MenuItem>
                        <MenuItem value="zero" sx={{ fontSize: '0.875rem' }}>Zero Rated</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      label="Rate of Tax (%)"
                      name="taxRate"
                      value={formData.taxRate}
                      fullWidth
                      margin="dense"
                      size="small"
                      placeholder="Enter tax rate"
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                      sx={controlStyle}
                    />
                  </Grid>
                </>
              )}

{/* Alternative UOM Tab */ }
{
  activeTab === 1 && (
    <Grid item xs={12} md={6}>
      <TextField
        label="Alternate Barcode"
        name="altBarcode"
        value={formData.altBarcode}
        onChange={handleChange}
        fullWidth
        margin="dense"
        size="small"
        placeholder="Enter alternate barcode"
        sx={controlStyle}
        autoComplete="off"
      />

      <FormControl fullWidth margin="dense" size="small">
        <InputLabel sx={{ fontSize: '0.875rem' }}>Alternate Unit</InputLabel>
        <Select
          name="alternateUnit"
          value={formData.alternateUnit}
          onChange={handleChange}
          label="Alternate Unit"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 0,
              height: 36,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: 0,
            },
            '& .MuiSelect-select': {
              py: 0.75,
              fontSize: '0.875rem',
              borderRadius: 0,
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.875rem'
            }
          }}
        >
          <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
            <em>None</em>
          </MenuItem>
          {units.map((unit) => (
            <MenuItem key={unit._id} value={unit._id} sx={{ fontSize: '0.875rem' }}>
              {unit.name} ({unit.symbol})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Alternate Purchase Rate"
        name="altPurchaseRate"
        value={formData.altPurchaseRate}
        onChange={handleChange}
        fullWidth
        margin="dense"
        size="small"
        type="number"
        placeholder="Enter alternate purchase rate"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <img src={image1} alt="currency" style={{ width: '15px', height: '15px' }} />
            </InputAdornment>
          ),
        }}
        sx={controlStyle}
        autoComplete="off"
      />

      <TextField
        label="Alternate Sales Rate"
        name="altSalesRate"
        value={formData.altSalesRate}
        onChange={handleChange}
        fullWidth
        margin="dense"
        size="small"
        type="number"
        placeholder="Enter alternate sales rate"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <img src={image1} alt="currency" style={{ width: '15px', height: '15px' }} />
            </InputAdornment>
          ),
        }}
        sx={controlStyle}
        autoComplete="off"
      />
    </Grid>
  )
}
            </Grid >
          </Paper >

  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
    <Button
      variant="outlined"
      onClick={handleCancel}
      startIcon={<CancelIcon />}
      disabled={loading}
    >
      Cancel
    </Button>
    <Button
      type="submit"
      variant="contained"
      startIcon={<SaveIcon />}
      disabled={loading}
    >
      {loading ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
    </Button>
  </Box>
        </form >
      </Paper >
    </Container >
  );
};

export default NewItem; 